// Core-100 R4 — CORE-AC: the browser asks; the SERVER derives a ticket's finish.
//
// `tickets.opti_finish` is no longer written from the browser (the table refuses an
// `authenticated` change of it: migration 2027021222010000). Every place that used to
// compute a finish here and write it — AutoBump, OptiFinish pushback, the import
// sequence normaliser, Add Job's custom path, the timer start — now states its INTENT
// to the Edge function `ticket-finish-intent`, which reads the ticket, technician,
// shop and measured sessions, derives the finish with the one derivation
// (supabase/functions/_shared/finishIntentDerivation.ts) and writes it as the server,
// compare-and-swapped against what it read.
//
// The caller may still compute a finish for DISPLAY or to decide whether to ask; what
// is persisted is the server's. The result carries the server's start/finish so a
// caller that needs them (AutoBump's split-sibling check) uses the persisted values.
import { supabase } from '@/integrations/supabase/client';

export type ServerFinishIntent =
  | 'bump_to_now'
  | 'run_long'
  | 'pause_extend'
  | 'eod_lock'
  | 'pushback'
  | 'place_from_start'
  | 'timer_started'
  // W4b: a vehicle the technician switched away from / one with a machine process running (server-derived).
  | 'switched_away'
  | 'unattended_hold';

export interface ServerFinishParams {
  setBaseline?: boolean;
  track?: 'run_long' | 'pause';
  lockAtEod?: boolean;
  when?: 'pause_grew' | 'finish_drifted';
  startTime?: string;
  notAfter?: string;
}

export interface ServerFinishResult {
  /** The server applied the intent, or found nothing to change. */
  success: boolean;
  /**
   * The server declined on the CURRENT row (not running long, moved since the caller
   * decided, chain-governed, duration unknown, a concurrent write won …). Nothing was
   * written and nothing failed; the caller re-decides on its next tick.
   */
  refused: boolean;
  outcome: string;
  data?: {
    startTime: string | null;
    optiFinish: string | null;
    previousOptiFinish: string | null;
    pushMinutes: number | null;
    detail: string | null;
  };
  error?: string;
}

const SUCCESS = new Set(['APPLIED', 'UNCHANGED']);
const REFUSED = new Set([
  'NOT_APPLICABLE',
  'DURATION_UNKNOWN',
  'NO_TECHNICIAN',
  'CHAIN_GOVERNED',
  'WOULD_OVERLAP',
  'STALE',
  'CONFLICT',
  'TICKET_NOT_AVAILABLE',
]);

export async function requestServerFinish(
  ticketId: string,
  intent: ServerFinishIntent,
  options: { reason: string; params?: ServerFinishParams; expectedStartTime?: string | null },
): Promise<ServerFinishResult> {
  const body: Record<string, unknown> = { ticketId, intent };
  if (options.params) body.params = options.params;
  if (options.expectedStartTime !== undefined) body.expectedStartTime = options.expectedStartTime;

  const { data, error } = await supabase.functions.invoke('ticket-finish-intent', { body });
  if (error) {
    const msg = `[ticket-finish-intent] ${intent} failed for ${ticketId}: ${error.message ?? String(error)} (reason=${options.reason})`;
    console.error(msg);
    return { success: false, refused: false, outcome: 'ERROR', error: msg };
  }
  const envelope = (data ?? {}) as { data?: Record<string, unknown> } & Record<string, unknown>;
  const r = (envelope.data ?? envelope) as Record<string, unknown>;
  const outcome = String(r.outcome ?? 'ERROR');
  const result: ServerFinishResult = {
    success: SUCCESS.has(outcome),
    refused: REFUSED.has(outcome),
    outcome,
    data: {
      startTime: (r.startTime as string | null | undefined) ?? null,
      optiFinish: (r.optiFinish as string | null | undefined) ?? null,
      previousOptiFinish: (r.previousOptiFinish as string | null | undefined) ?? null,
      pushMinutes: typeof r.pushMinutes === 'number' ? r.pushMinutes : null,
      detail: (r.detail as string | null | undefined) ?? null,
    },
  };
  if (!result.success && !result.refused) {
    result.error = `[ticket-finish-intent] ${intent} returned ${outcome} for ${ticketId} (reason=${options.reason})`;
    console.error(result.error);
  }
  return result;
}
